/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-08-08 10:21:40
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Modal, TextContainer, FormLayout } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { Field, reduxForm, reset } from 'redux-form';

import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import * as supportTicketActions from 'actions/support-ticket-actions';
import { isRequired, isEmail, isExceedLength } from 'src/helpers/form-validation-helper';
import { prettyText } from 'src/helpers';

const isRequiredValue = isRequired();
const isEmailValue = isEmail();
const isExceedLengthValue = isExceedLength(10000);

type Props = {
  active: boolean,
  removing: boolean,
  handleChange: () => void,
  sendSupportTicket: () => void
};

type ComposedProps = Props & WithI18nProps;

class SendRequestModal extends Component<ComposedProps> {
  componentWillReceiveProps(nextProps) {
    if (nextProps.success === true && nextProps.activeSendRequestModal === true) {
      // Reset form
      this.props.resetForm();

      // Reset all states
      this.props.resetSendSupportTicket();

      // Close modal
      this.props.handleChange();
    }
  }

  render() {
    const { active, pristine, submitting, handleSubmit, sendRequestLoading, i18n } = this.props;

    return (
      <Modal
        open={active}
        onClose={this.handleChange}
        title={i18n.translate('AdminHTML.Common.SendRequestModal.modalTitle')}
        primaryAction={{
          content: i18n.translate('AdminHTML.Common.Action.send'),
          disabled: pristine || submitting,
          loading: sendRequestLoading && !pristine,
          onAction: handleSubmit(this.handleSendRequest.bind(this))
        }}
        secondaryActions={[
          {
            content: i18n.translate('AdminHTML.Common.Action.cancel'),
            onAction: this.handleChange
          }
        ]}>
        <Modal.Section>
          <TextContainer>
            <form onSubmit={this.handleDefaultSubmit}>
              <FormLayout>
                <Field
                  name="email"
                  label={i18n.translate('AdminHTML.Common.FieldLabel.yourEmail')}
                  placeholder={i18n.translate('AdminHTML.Common.Placeholder.email')}
                  component={RenderTextField}
                  validate={[isRequiredValue, isEmailValue, isExceedLengthValue]}
                />
                <Field
                  name="content"
                  label={i18n.translate('AdminHTML.Common.SendRequestModal.fieldLabel_Content')}
                  component={RenderTextField}
                  placeholder={i18n.translate(
                    'AdminHTML.Common.SendRequestModal.fieldPlaceholder_Content'
                  )}
                  anotherProps={{ multiline: 5 }}
                  validate={[isRequiredValue, isExceedLengthValue]}
                />
              </FormLayout>
            </form>
          </TextContainer>
        </Modal.Section>
      </Modal>
    );
  }

  // Handle default form submit
  handleDefaultSubmit(e) {
    e.preventDefault();
  }

  handleChange = () => {
    this.props.handleChange();
    this.props.resetForm();
  };

  handleSendRequest = async data => {
    const { sendSupportTicket } = this.props;

    const prettyData = {
      email: prettyText(data.email),
      content: prettyText(data.content),
      selection: {}
    };
    const result = await sendSupportTicket(prettyData);

    if (result.type === 'SEND_SUPPORT_TICKET_SUCCESS') {
      this.props.handleChange();
    }
  };
}

const mapStateToProps = (state, props) => {
  const { supportTicket } = state;
  return {
    sendRequestLoading: _.get(supportTicket, 'sendSupportTicketLoading', false),
    sendRequestSuccess: _.get(supportTicket, 'sendSupportTicketSuccess', false)
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  sendSupportTicket: data =>
    dispatch(supportTicketActions.sendSupportTicket(data, [], ownProps.i18n)),
  resetSendSupportTicket: () => dispatch(supportTicketActions.resetSendSupportTicket()),
  resetForm: () => dispatch(reset('SendRequestModal'))
});

const SendRequestModalForm = reduxForm({
  form: 'SendRequestModal', // a unique identifier for this form
  enableReinitialize: true
})(SendRequestModal);

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(SendRequestModalForm));
